import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';

import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { isTooManyRequestsError, storableError } from '../../util/errors';
import { RATE_LIMIT_BACKOFF_MAX_MS, RATE_LIMIT_BACKOFF_MS } from '../../util/messagePolling';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { ASPECT_HEIGHT_LISTING, ASPECT_WIDTH_LISTING } from '../../config/configLayout';

import { buildConversationsFromTransactions } from './messagesQuery';

const RECENT_TX_PAGE_SIZE = 25;

/** Module-level guards so Topbar / list / mark-read share one in-flight fetch + backoff. */
let fetchRecentMessagesInFlight = null;
let rateLimitedUntil = 0;
let rateLimitBackoffMs = RATE_LIMIT_BACKOFF_MS;

// ================ Action types ================ //

export const FETCH_RECENT_MESSAGES_REQUEST = 'app/MessagesPage/FETCH_RECENT_MESSAGES_REQUEST';
export const FETCH_RECENT_MESSAGES_SUCCESS = 'app/MessagesPage/FETCH_RECENT_MESSAGES_SUCCESS';
export const FETCH_RECENT_MESSAGES_ERROR = 'app/MessagesPage/FETCH_RECENT_MESSAGES_ERROR';
export const FETCH_RECENT_MESSAGES_SETTLE = 'app/MessagesPage/FETCH_RECENT_MESSAGES_SETTLE';

// ================ Reducer ================ //

const entityRef = entity => ({
  id: entity.id,
  type: entity.type,
});

const latestMessageTime = conversation => conversation.latestMessage?.attributes?.createdAt || null;

const sortedConversations = conversations =>
  reverse(
    sortBy(conversations, conversation => {
      return latestMessageTime(conversation);
    })
  );

const initialState = {
  fetchInProgress: false,
  fetchRecentMessagesError: null,
  conversations: [],
};

export default function messagesPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_RECENT_MESSAGES_REQUEST:
      return { ...state, fetchInProgress: true, fetchRecentMessagesError: null };
    case FETCH_RECENT_MESSAGES_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        fetchRecentMessagesError: null,
        conversations: sortedConversations(payload.conversations),
      };
    case FETCH_RECENT_MESSAGES_SETTLE:
      // Soft failure (e.g. 429): stop spinner, keep last conversations, no error banner.
      return { ...state, fetchInProgress: false };
    case FETCH_RECENT_MESSAGES_ERROR:
      console.error(payload);
      return { ...state, fetchInProgress: false, fetchRecentMessagesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchRecentMessagesRequest = () => ({ type: FETCH_RECENT_MESSAGES_REQUEST });
const fetchRecentMessagesSuccess = conversations => ({
  type: FETCH_RECENT_MESSAGES_SUCCESS,
  payload: { conversations },
});
const fetchRecentMessagesSettle = () => ({ type: FETCH_RECENT_MESSAGES_SETTLE });
const fetchRecentMessagesError = e => ({
  type: FETCH_RECENT_MESSAGES_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

const listingCardAspectRatio = ASPECT_HEIGHT_LISTING / ASPECT_WIDTH_LISTING;

const imageVariants = {
  'fields.image': [
    'variants.square-small',
    'variants.square-small2x',
    'variants.listing-card',
    'variants.listing-card-2x',
  ],
  ...createImageVariantConfig('listing-card', 400, listingCardAspectRatio),
  ...createImageVariantConfig('listing-card-2x', 800, listingCardAspectRatio),
};

/**
 * One query per role (order + sale). Include messages so we avoid N+1
 * messages.query calls that rate-limit practitioners to an empty list.
 */
const transactionQueryParams = only => ({
  only,
  lastTransitions: getAllTransitionsForEveryProcess(),
  include: [
    'listing',
    'listing.images',
    'provider',
    'provider.profileImage',
    'customer',
    'customer.profileImage',
    'messages',
    'messages.sender',
    'messages.sender.profileImage',
  ],
  'fields.transaction': [
    'processName',
    'lastTransition',
    'lastTransitionedAt',
    'createdAt',
    'transitions',
    'protectedData',
  ],
  // Keep process/unitType so sparse merges never drop ListingPage-required publicData.
  'fields.listing': [
    'title',
    'publicData.listingType',
    'publicData.transactionProcessAlias',
    'publicData.unitType',
  ],
  'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
  'fields.message': ['content', 'createdAt'],
  ...imageVariants,
  page: 1,
  perPage: RECENT_TX_PAGE_SIZE,
});

const queryTransactions = sdk => {
  return Promise.all([
    sdk.transactions.query(transactionQueryParams('order')),
    sdk.transactions.query(transactionQueryParams('sale')),
  ]);
};

const noteRateLimit = () => {
  rateLimitedUntil = Date.now() + rateLimitBackoffMs;
  rateLimitBackoffMs = Math.min(rateLimitBackoffMs * 2, RATE_LIMIT_BACKOFF_MAX_MS);
};

const clearRateLimit = () => {
  rateLimitedUntil = 0;
  rateLimitBackoffMs = RATE_LIMIT_BACKOFF_MS;
};

/**
 * Fetch recent conversations for Messages list + topbar unread badge.
 * Soft-fails (never rethrows): safe to call from polling / useEffect.
 * Dedupes concurrent callers and backs off after HTTP 429.
 *
 * @param {Object} [options]
 * @param {boolean} [options.ignoreRateLimit] - Bypass soft-poll backoff (mount,
 *   sendMessage, explicit load). Poll interval keeps respecting backoff.
 */
export const fetchRecentMessages = (options = {}) => (dispatch, getState, sdk) => {
  const { ignoreRateLimit = false } = options;

  if (fetchRecentMessagesInFlight) {
    return fetchRecentMessagesInFlight;
  }

  if (!ignoreRateLimit && Date.now() < rateLimitedUntil) {
    return Promise.resolve(null);
  }

  dispatch(fetchRecentMessagesRequest());

  fetchRecentMessagesInFlight = queryTransactions(sdk)
    .then(responses => {
      responses.forEach(response => dispatch(addMarketplaceEntities(response)));
      const transactions = responses.reduce((acc, response) => {
        return acc.concat(denormalisedResponseEntities(response));
      }, []);

      const currentUserId = getState().user?.currentUser?.id?.uuid;
      const conversations = buildConversationsFromTransactions(transactions, { currentUserId }).map(
        conversation => ({
          transactionRef: entityRef(conversation.transaction),
          latestMessage: conversation.latestMessage,
        })
      );

      clearRateLimit();
      dispatch(fetchRecentMessagesSuccess(conversations));
      return conversations;
    })
    .catch(e => {
      const stored = storableError(e);
      if (isTooManyRequestsError(e) || isTooManyRequestsError(stored)) {
        noteRateLimit();
        dispatch(fetchRecentMessagesSettle());
        return null;
      }
      dispatch(fetchRecentMessagesError(stored));
      // Soft failure — do not rethrow (avoids CRA overlay from useEffect / polling).
      return null;
    })
    .finally(() => {
      fetchRecentMessagesInFlight = null;
    });

  return fetchRecentMessagesInFlight;
};

export const loadData = () => dispatch => {
  return dispatch(fetchRecentMessages({ ignoreRateLimit: true }));
};
